import { useCallback } from "react";
import { styled } from "styled-components";

import {
  BankConnectionDisplay,
  type BankConnection,
  type BankReconnectFunction,
  type LogoLoadingState,
} from "../bank-connection-display";
import type { BankConnectCountryConfig, BankConnectFunction } from "../connect-bank-button";
import { ConnectBankButton } from "../connect-bank-button";

import { useBankConnectConsent } from "./contexts/useBankConnectConsent";

const BankConnectionsContainer = styled.div`
  display: flex;
  flex-direction: row;
  margin-top: var(--sizes-spacing-2);
  margin-bottom: calc(var(--sizes-spacing-2) * -1);
  justify-content: flex-start;
  flex-wrap: wrap;

  @media (max-width: 640px) {
    justify-content: space-between;
  }
`;
export interface LogoLoadingStates {
  [institutionID: string]: LogoLoadingState;
}

export interface UseBankConnectState {
  bankConnections: BankConnection[];
  startReconnect: BankReconnectFunction;
  startConnect: BankConnectFunction;
  logoLoadingStates: LogoLoadingStates;
  providerName: string;
}

export interface BankConnectionsManagerProps {
  useBankConnect: () => UseBankConnectState;
  className?: string;
  useConsentModal?: boolean;
  countryOptions?: BankConnectCountryConfig[];
}

const MAX_CONNECTIONS = 6;

/**
 * BankConnectionsManager component
 *
 * TODO: deprecate and migrate to wayflyer shared-ui
 */
export const BankConnectionsManager = ({
  useBankConnect,
  className,
  useConsentModal = true,
}: BankConnectionsManagerProps) => {
  const { bankConnections, startConnect, startReconnect, logoLoadingStates, providerName } = useBankConnect();
  const { bankConnectConsentNeeded, openBankConnectConsentModal } = useBankConnectConsent();
  const bankConnectionsToRender = bankConnections.slice(0, MAX_CONNECTIONS);

  const startConnectWrapper = useCallback(
    (ev) => {
      // if country selection enabled, bypass the old consent modal flow
      if (useConsentModal && bankConnectConsentNeeded) {
        openBankConnectConsentModal(providerName, startConnect);
      } else {
        startConnect(ev);
      }
    },
    [bankConnectConsentNeeded, openBankConnectConsentModal, providerName, startConnect, useConsentModal]
  );

  const startReconnectWrapper = useCallback(
    (bankConnection) => {
      if (bankConnectConsentNeeded) {
        openBankConnectConsentModal(providerName, () => {
          startReconnect(bankConnection);
        });
      } else {
        startReconnect(bankConnection);
      }
    },
    [bankConnectConsentNeeded, openBankConnectConsentModal, providerName, startReconnect]
  );

  return (
    <BankConnectionsContainer className={className}>
      {bankConnectionsToRender.map((bankConnection, index) => (
        <BankConnectionDisplay
          key={index}
          bankConnection={bankConnection}
          logoState={logoLoadingStates[bankConnection.institutionID]}
          startReconnect={startReconnectWrapper}
        />
      ))}
      {bankConnectionsToRender.length < MAX_CONNECTIONS && (
        <ConnectBankButton key="blank" startConnect={startConnectWrapper} />
      )}
    </BankConnectionsContainer>
  );
};
